import useAppStore from "@/context/store";
import { useEventListener, useMemoizedFn } from "ahooks";
import { useRouter } from "next/router";
import { useEffect, useRef } from "react";
import { getModalList, type IModalInstance } from "../HBModal/ModalWrap";

export default function useGlobalUploader() {
  const router = useRouter();
  const { pathname } = router;
  const user = useAppStore((s) => s.user);

  const uploaderInstanceRef = useRef<IModalInstance>();

  const openGlobalUploader = useMemoizedFn(async () => {
    const { GlobalUploader } = await import("./index");

    if (uploaderInstanceRef.current) {
      return;
    }

    uploaderInstanceRef.current = GlobalUploader.open();
  });

  const closeGlobalUploader = useMemoizedFn(() => {
    if (uploaderInstanceRef.current) {
      uploaderInstanceRef.current.destroy();
      uploaderInstanceRef.current = null;
    }
  });

  useEventListener("paste", async (event) => {
    if (window._search_similar_img_visible || !user) {
      return;
    }

    // 在输入类型组件里不触发粘贴事件
    const target = event.target as HTMLElement;
    if (
      target?.tagName === "INPUT" ||
      target?.tagName === "TEXTAREA" ||
      target.hasAttribute("contenteditable") ||
      pathname === "/create"
    ) {
      return;
    }

    const items = event.clipboardData && event.clipboardData.items;
    let files = [];
    if (items && items.length) {
      // 检索剪切板items
      for (var i = 0; i < items.length; i++) {
        if (items[i].type.startsWith("image/")) {
          files.push(items[i].getAsFile());
        }
      }
    }
    if (files.length > 0) {
      const { uploadImages } = await import("./index");
      uploadImages({ fileList: files });
    }
  });

  useEffect(() => {
    // 判断是否是内部拖拽
    let isInner = false;
    const onDragEnter = () => {
      const modalList = getModalList();
      const modal = modalList.find((modal) =>
        ["pinUploaderModal", "boardEditModal"].includes(modal.name)
      );
      if (
        // 上传采集弹窗和画板编辑弹窗是否展示
        !modal &&
        // 用户是否登录
        user &&
        // 判断是否是内部拖拽
        !isInner &&
        // 判断是否是认证设计师页
        pathname !== "/muse/certify" &&
        pathname !== "/create" &&
        // 以图搜图窗口是否展示，原因是以图搜图跟全局上传采集是互斥的，然后如果使用context去传参，会增加其他模块的render次数，不太合适，所以最合适就是使用全局变量，不会影响服务端渲染
        !window._search_similar_img_visible
      ) {
        openGlobalUploader();
      }
    };
    const onMouseLeave = () => {
      // 这个用setTimeout主要为了解决快速操作导致的问题，确保最后才执行，但是safari依然有几率不会触发mouseleave
      setTimeout(() => {
        closeGlobalUploader();
      });
    };
    // 为了解决chorme只要拖动内部元素再从外拖到内就不会触发mouseleave，对safari不生效
    const onDragLeave = (evt) => {
      if (!evt.clientX && !evt.clientY) {
        closeGlobalUploader();
      }
    };
    const onDragStart = () => {
      isInner = true;
    };
    const onDragEnd = () => {
      isInner = false;
    };

    document.documentElement.addEventListener("dragenter", onDragEnter);
    document.documentElement.addEventListener("dragstart", onDragStart);
    document.documentElement.addEventListener("dragend", onDragEnd);
    document.documentElement.addEventListener("mouseleave", onMouseLeave);
    document.documentElement.addEventListener("dragleave", onDragLeave);

    return () => {
      document.documentElement.removeEventListener("dragenter", onDragEnter);
      document.documentElement.removeEventListener("dragstart", onDragStart);
      document.documentElement.removeEventListener("dragend", onDragEnd);
      document.documentElement.removeEventListener("mouseleave", onMouseLeave);
      document.documentElement.addEventListener("dragleave", onDragLeave);
    };
  }, [user, pathname, openGlobalUploader, closeGlobalUploader]);
}
